import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import apiHelpers from '../../utils/apiHelpers';
import { useCollection } from './CollectionContext';
import ImportCollectionModal from './ImportCollectionModal';
import CardList from '../findCard/CardList';
import CardFilters from '../card/CardFilters';
import Loader from '../common/Loader';
import Button from '../common/Button';
import Input from '../common/Input';
import Alert from '../common/Alert';

const PAGE_SIZE = 60;

const Collection = () => {
  const { reload } = useCollection();

  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [cardName, setCardName] = useState('');
  // { where, advanced } — one object from the unified CardFilters.
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showCards, setShowCards] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [debouncedCardName] = useDebounce(cardName, 1000);
  const [errorMessage, setErrorMessage] = useState('');

  const runSearch = useCallback(
    async append => {
      const targetPage = append ? page + 1 : 1;
      setLoading(true);
      setErrorMessage('');
      try {
        const advanced = filters.advanced || {};
        const where = { ...(filters.where || {}) };
        if (debouncedCardName.trim()) where.name = debouncedCardName.trim();
        if (advanced.colors) where.colors = advanced.colors;
        if (advanced.colorMode) where.colorMode = advanced.colorMode;
        if (advanced.cmc !== null && advanced.cmc !== undefined) {
          where.cmc = advanced.cmc;
          where.cmcComparator = advanced.cmcComparator;
        }

        const response = await apiHelpers.getCollection({
          where,
          limit: PAGE_SIZE,
          skip: (targetPage - 1) * PAGE_SIZE,
        });

        const newCards = response.data.cards || [];
        setCards(prevCards => {
          const merged = append ? prevCards.concat(newCards) : newCards;
          return merged.filter(
            (card, index, self) =>
              index === self.findIndex(c => c.id === card.id),
          );
        });
        setTotal(response.data.total || 0);
        setHasMore(Boolean(response.data.hasMore));
        setPage(targetPage);
        setLoading(false);
      } catch (error) {
        setLoading(false);

        if (error.response) {
          const { status, data } = error.response;
          if (status === 400 && data?.message) {
            setErrorMessage(data.message);
          } else if ([400, 422, 204].includes(status)) {
            setErrorMessage('Error al cargar la colección');
          } else if (error.request) {
            setErrorMessage('Sin respuesta del Servidor');
          } else {
            setErrorMessage(error.message);
          }
        } else {
          setErrorMessage('Error de conexión con el servidor');
        }
      }
    },
    [filters, debouncedCardName, page],
  );

  useEffect(() => {
    // CardFilters reports its (empty) filters in a mount effect, one render
    // after ours — without this guard the first load would run with no `where`.
    if (!filters.where) return;
    runSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCardName, filters]);

  const handleChangeFilters = newFilters => {
    setFilters(newFilters);
  };

  const handleImported = () => {
    reload();
    runSearch(false);
  };

  return (
    <div className="flex flex-col gap-y-4">
      <h1 className="text-2xl text-center">
        <i className="fas fa-boxes mr-2"></i>
        Colección
      </h1>
      <h4>
        Todas las cartas que tenés, con la cantidad de copias de cada una.
        Agregá cartas desde "Buscar carta", "Buscar lista de cartas" o tu
        wishlist, o importá tu colección de Moxfield desde un archivo CSV.
      </h4>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="w-full sm:w-1/2">
          <Input
            name="card_name"
            placeholder='Filtrar por nombre. Por ejemplo, "Llanowar elves"'
            value={cardName}
            onChange={e => setCardName(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-1/2">
          <Button
            className="w-full"
            iconClass="fas fa-file-csv"
            onClick={() => setShowImport(true)}>
            Importar desde Moxfield (CSV)
          </Button>
        </div>
      </div>

      <CardFilters
        loadData={handleChangeFilters}
        fields={[
          'condition',
          'priceRank',
          'language',
          'price',
          'types',
          'stores',
          'colors',
          'cmc',
        ]}
      />

      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          className="w-full sm:w-1/2"
          iconClass="fas fa-id-card"
          onClick={() => setShowCards(true)}
          disabled={loading}>
          Ver en grilla
        </Button>
        <Button
          className="w-full sm:w-1/2"
          iconClass="fas fa-list-alt"
          onClick={() => setShowCards(false)}
          disabled={loading}>
          Ver en lista
        </Button>
      </div>

      <div className="flex justify-between items-center bg-mediumColor p-3.5 rounded-md font-bold">
        <span className="text-lg text-white">
          Cartas distintas en la colección: {total}
        </span>
      </div>

      <Alert>{errorMessage}</Alert>
      {loading && cards.length === 0 && <Loader />}
      {!loading && cards.length === 0 && !errorMessage && (
        <p className="text-gray-400">
          Todavía no hay cartas en tu colección con estos filtros.
        </p>
      )}
      <CardList cards={cards} showCards={showCards} />
      {loading && cards.length > 0 && <Loader />}
      {!loading && hasMore && (
        <Button iconClass="fas fa-chevron-down" onClick={() => runSearch(true)}>
          Cargar más resultados
        </Button>
      )}

      <ImportCollectionModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={handleImported}
      />
    </div>
  );
};

export default Collection;
